import React, {Component} from 'react';
import logo from './images/logo_dashbo_purple.svg';
import logoParsed from './images/logo_parsed.png';
import {Link, Navigate} from "react-router-dom";
import {FaIcon} from "./components/FaIcon";
import {LocalStorage} from "./utils/LocalStorage";
import {HelpButton} from './components/agency/HelpButton';
import {AccessService} from "./utils/AccessService";
import {isClientReportsModality, readReportModality} from "./hooks/useReportModality";
import {APIClient} from "./utils/APIClient";
import {API_URL} from "./settings/local_settings";
import SuperAdminSwitcher from "./components/super_admin/SuperAdminSwitcher";
import {KnewsBell} from "./components/knews/KnewsBell";
import { withTranslation } from 'react-i18next';
import {useUiVersion} from "./context/UserModulesContext";
import {BackToUiV2Button} from "./components/layout/v2/BackToUiV2Button";
import {AppChromeV2Skeleton} from "./components/layout/v2/AppChromeV2Skeleton";
import {anchorAttr} from "./guides/anchors";
import {useApplyUserLanguage} from "./hooks/useApplyUserLanguage";

class LayoutSidebarClass extends Component {
    constructor(props) {
        super(props);

        this.state = {
            loggedOut: false,
            showOnBoarding: false,
            modules: null,
            isSuperAdmin: false
        };

        this.handleLogout = this.handleLogout.bind(this);
        this.handleGetUserModulesError = this.handleGetUserModulesError.bind(this);
        this.handleGetUserModules = this.handleGetUserModules.bind(this);
    }

    handleGetUserModules(data) {
        this.setState({
            ...this.state,
            modules: data.modules
        });
    }

    handleGetUserModulesError(data) {
        alert(data['error_message']);
        LocalStorage.removeUserData();
        this.setState({loggedOut: true});
    }

    getUserModules() {
        AccessService.GetUserModules(this.getUserId(), this.handleGetUserModules, this.handleGetUserModulesError)
    }

    componentDidMount() {
        this.getUserModules();
        this.fetchSuperAdminFlag();
    }

    fetchSuperAdminFlag() {
        new APIClient(API_URL + 'get-logged-user-data/').call('GET', {}, {cache: true}).then((data) => {
            if (data && data.status === 'OK') {
                const isSuperAdmin = data.is_super_admin === true;
                this.setState({isSuperAdmin});
                LocalStorage.updateIsSuperAdmin(isSuperAdmin);
                if (data.agency_id) {
                    LocalStorage.updateAgencyId(String(data.agency_id));
                }
                // En el marco clásico nadie más persiste la modalidad (el hook sólo vive en el
                // 2.1): sin esto la lupa iba a `/data-explorer` hasta abrir una ficha (C-informes-21).
                const reportModality = data.agency_report_modality || 'DATA_EXPLORER';
                LocalStorage.updateAgencyReportModality(reportModality, data.agency_id);
                this.setState({reportModality});
            }
        });
    }

    handleLogout() {
        LocalStorage.removeUserData();
        this.setState({loggedOut: true});
    }

    _loginPath() {
        return {pathname: '/login'};
    }

    renderRedirectToLogin() {
        if (this.state.loggedOut) {
            return <Navigate to={this._loginPath()}/>
        }
    }

    getUserId() {
        return LocalStorage.fetchUserId()
    }

    renderManageAccountsIcon() {
        if(this.state.modules.includes(AccessService.MODULE_MANAGE_ACCOUNTS)) {
            return <Link className="sidebar-main-link" to="/manage-accounts"><FaIcon iconName="building"/></Link>
        }
    }

    renderMarketingIcon() {
        const { t } = this.props;
        if(this.state.modules.includes(AccessService.MODULE_MARKETING)) {
            return <Link className="sidebar-main-link" to="/marketing" {...anchorAttr('sidebar_dashboard_classic')}><FaIcon iconName="chart-line"/><span
                className="tooltip-text">{t('layout.dashboard')}</span></Link>
        }
    }

    renderDataExplorerIcon() {
        const { t } = this.props;
        const hasAccess = this.state.modules.includes(AccessService.MODULE_DATA_EXPLORER);
        // Coexistencia por agencia (frente Informes): la modalidad decide CUÁL pantalla se ofrece
        // en este mismo lugar del menú; el módulo (el candado) decide SI hay pantalla.
        const isInformes = isClientReportsModality(this.state.reportModality || readReportModality());
        const explorerPath = isInformes ? '/informes' : '/data-explorer';

        if (hasAccess) {
            return (
                <Link className="sidebar-main-link" to={explorerPath}>
                    <FaIcon iconName="binoculars"/>
                    <span className="tooltip-text">{t(isInformes ? 'layout.informes' : 'layout.data_explorer')}</span>
                </Link>
            );
        }

        return (
            <Link className="sidebar-main-link -locked" to={explorerPath}>
                <div className="sidebar-locked-icon-wrapper">
                    <FaIcon iconName="binoculars"/>
                    <span className="sidebar-lock-badge">
                        <FaIcon iconName="lock"/>
                    </span>
                </div>
                <span className="tooltip-text">{t(isInformes ? 'layout.informes' : 'layout.data_explorer_locked')}</span>
            </Link>
        );
    }

    renderAgentChatIcon() {
        const { t } = this.props;
        if(this.state.modules.includes(AccessService.MODULE_AGENT)) {
            return <Link className="sidebar-main-link" to="/agent/chat"><FaIcon iconName="robot"/><span
                className="tooltip-text">{t('layout.agent_chat')}</span></Link>
        }
    }

    renderReportIcon() {
        const { t } = this.props;
        if(this.hasAccessToInsightsModule()) {
            return <Link className="sidebar-main-link" to="/insights"><FaIcon iconName="star"/><span
                className="tooltip-text">{t('layout.insights_reports')}</span></Link>
        }
    }

    renderAllReportsIcon() {
        const { t } = this.props;
        if(this.state.modules.includes(AccessService.MODULE_REPORTS)) {
            return <Link className="sidebar-main-link" to="/reports"><FaIcon iconName="wand-magic-sparkles"/><span
                className="tooltip-text">{t('layout.all_reports')}</span></Link>
        }
    }

    renderDashboMCPIcon() {
        const { t } = this.props;
        const hasAccess = this.state.modules.includes(AccessService.MODULE_DASHBO_MCP);

        if (hasAccess) {
            return (
                <Link className="sidebar-main-link" to="/dashbo-mcp">
                    <FaIcon iconName="microchip"/>
                    <span className="tooltip-text">{t('layout.dashbo_mcp')}</span>
                </Link>
            );
        }

        return (
            <Link className="sidebar-main-link -locked" to="/dashbo-mcp">
                <div className="sidebar-locked-icon-wrapper">
                    <FaIcon iconName="microchip"/>
                    <span className="sidebar-lock-badge">
                        <FaIcon iconName="lock"/>
                    </span>
                </div>
                <span className="tooltip-text">{t('layout.dashbo_mcp_locked')}</span>
            </Link>
        );
    }

    hasAccessToInsightsModule() {
        return this.state.modules.includes(AccessService.MODULE_INSIGHTS);
    }

    renderMyClientsIcon() {
        const { t } = this.props;
        if(this.state.modules.includes(AccessService.MODULE_TEAM)) {
            // "Mis Clientes" apunta a la asignación del usuario (launcher + editor), no a /clients/.
            const myClientsUrl = "/user/" + LocalStorage.fetchUserId() + "/module/marketing";
            return <Link className="sidebar-main-link" to={myClientsUrl} ><FaIcon iconName="handshake"/><span
                className="tooltip-text">{t('layout.my_clients')}</span></Link>
        }
    }

    renderSheetsSyncIcon() {
        const { t } = this.props;
        if(this.state.modules.includes(AccessService.MODULE_SHEETS_SYNC)) {
            return <Link className="sidebar-main-link" to={"/sheets-sync"} ><FaIcon iconName="file-export"/><span
                className="tooltip-text">{t('layout.data_export')}</span></Link>
        }
    }

    renderSuperAdminSwitcher() {
        if (this.state.isSuperAdmin) {
            return <SuperAdminSwitcher/>;
        }
        return null;
    }

    renderAgencyIcon() {
        const { t } = this.props;
        const hasMarketingModule = this.state.modules.includes(AccessService.MODULE_MARKETING);
        const url = hasMarketingModule ? "/agency/clients" : "/agency/conections";
        return (<Link className="sidebar-main-link" to={url}><FaIcon iconName="cogs"/><span
            className="tooltip-text">{t('layout.agency_configuration')}</span></Link>);
    }

    isClientUser() {
        return LocalStorage.isClientUser();
    }

    getLogo(){
        // Para usuarios cliente, usar el logo de la agencia si está configurado
        if (this.isClientUser()) {
            const agencyLogoUrl = LocalStorage.fetchAgencyLogoUrl();
            if (agencyLogoUrl) return agencyLogoUrl;
        }
        if(this.hasAccessToInsightsModule())
            return logoParsed;
        return logo;
    }

    render() {
        const { t } = this.props;

        // CLIENT_USER usa el portal layout — no renderizar sidebar
        if (this.isClientUser()) {
            return null;
        }

        if (this.state.modules == null)
            return null

        return (
            <header className="layout-sidebar">
                <div className="top">
                    <Link to="/">
                        <img src={this.getLogo()} className="app-logo" alt="logo"/>
                    </Link>
                    {this.renderMarketingIcon()}
                    {this.renderDataExplorerIcon()}
                    {this.renderAgentChatIcon()}
                    {this.renderReportIcon()}
                    {this.renderAllReportsIcon()}
                    {this.renderDashboMCPIcon()}
                    {this.renderManageAccountsIcon()}
                    {this.renderSheetsSyncIcon()}
                    {this.renderMyClientsIcon()}
                    <Link className="sidebar-main-link" to={"/user/" + this.getUserId()}><FaIcon iconName="user"/><span
                        className="tooltip-text">{t('layout.my_profile')}</span></Link>
                </div>
                <div className="top">
                    <BackToUiV2Button/>
                    <KnewsBell enabled={this.state.isSuperAdmin}/>
                    {this.renderSuperAdminSwitcher()}
                    {this.renderAgencyIcon()}
                    <HelpButton showOnBoarding={this.props.showOnBoarding}/>
                    <button onClick={this.handleLogout} className="logout-button"><FaIcon iconName="power-off"/><span className="tooltip-text">{t('layout.logout')}</span></button>
                    {this.renderRedirectToLogin()}
                </div>
            </header>
        );
    }
}

const LayoutSidebarV1 = withTranslation()(LayoutSidebarClass);

// El marco nuevo (sidebar + topbar de UX 2.0) se carga aparte: así no viaja en el bundle de las
// agencias que no están en el piloto. El esqueleto sí es estático —pesa lo que pesan dos divs— y
// existe para que ese chunk no llegue con un salto de layout: ver `AppChromeV2Skeleton`.
const AppChromeV2 = React.lazy(() => import("./components/layout/v2/AppChromeV2"));

/**
 * Punto de montaje del marco de la app. `LayoutSidebar` ya está en las 54 pantallas que llevan
 * marco (y NO en el portal, el CRM ni las públicas), así que bifurcar acá alcanza para que toda la
 * app entre en la UI v2 sin tocar ni el ruteo ni las pantallas. Con el flag OFF devuelve
 * exactamente el sidebar de siempre.
 */
export function LayoutSidebar(props) {
    const {isV2} = useUiVersion();
    useApplyUserLanguage();

    if (isV2) {
        return (
            <React.Suspense fallback={<AppChromeV2Skeleton/>}>
                <AppChromeV2/>
            </React.Suspense>
        );
    }

    return <LayoutSidebarV1 {...props}/>;
}

export default LayoutSidebar;